import { gql } from '@apollo/client';
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconComment from '@apollo/icons/default/IconComment.svg';
import { Button, Card, CardBody, Divider, Heading, Link } from '@apollo/orbit';
import { NotFound } from '@apollo/space-kit/illustrations/NotFound';
import React from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { proposalHomepageRouteConfig } from 'src/app/graph/routes';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { useGetUsersForMentions } from '../../CommentMarkdownEditor/useMentionUsers';
import {
  ActivityFeedCommentThread,
  ActivityFeedCommentThreadChangeProposalCommentFragment,
  ActivityFeedCommentThreadGeneralProposalCommentFragment,
  ActivityFeedCommentThreadReviewCommentFragment,
} from '../activityFeed/ActivityFeedCommentThread';

export const SingleDeepLinkedProposalCommentProposalFragment = gql`
  fragment SingleDeepLinkedProposalCommentProposalFragment on Proposal {
    id
    comment(id: $threadId) @include(if: $shouldShowDeepLinkedComment) {
      ...ActivityFeedCommentThreadChangeProposalCommentFragment
      ...ActivityFeedCommentThreadGeneralProposalCommentFragment
      ...ActivityFeedCommentThreadReviewCommentFragment
    }
  }
  ${ActivityFeedCommentThreadChangeProposalCommentFragment}
  ${ActivityFeedCommentThreadGeneralProposalCommentFragment}
  ${ActivityFeedCommentThreadReviewCommentFragment}
`;

export const SingleDeepLinkedProposalComment = ({
  proposal,
  graphRef,
  usersForMentions,
}: {
  proposal: GraphQLTypes.UseAddCommentProposalFragment &
    GraphQLTypes.SingleDeepLinkedProposalCommentProposalFragment;
  graphRef: GraphRef;
  usersForMentions: ReturnType<typeof useGetUsersForMentions>;
}) => {
  const location = useLocation();

  return (
    <>
      <Divider className="my-6" />
      {proposal.comment?.__typename === 'ParentChangeProposalComment' ||
      proposal.comment?.__typename === 'ParentGeneralProposalComment' ||
      proposal.comment?.__typename === 'ReviewProposalComment' ? (
        <>
          <Link
            to={proposalHomepageRouteConfig.locationFrom({
              location,
              patch: {
                focusCommentId: RouteConfig.DELETE,
                focusThreadId: RouteConfig.DELETE,
              },
            })}
          >
            <IconArrowLeft className="mr-1" /> Back to all activity
          </Link>
          <div className="mt-6 flex items-start">
            <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-primary bg-primary">
              <IconComment className="text-icon-primary" />
            </div>

            <ActivityFeedCommentThread
              graphRef={graphRef}
              proposal={proposal}
              className="w-full p-0"
              comment={proposal.comment}
              usersForMentions={usersForMentions}
            />
          </div>
        </>
      ) : (
        <Card>
          <CardBody className="items-center">
            <NotFound />
            <Heading as="h3" size="xl" className="text-center">
              Something went wrong
            </Heading>
            <p className="w-[60ch] text-center text-base text-secondary">
              The comment you're looking for is no longer available.
            </p>
            <Button
              as={ReactRouterLink}
              to={proposalHomepageRouteConfig.locationFrom({
                location,
                patch: {
                  focusCommentId: RouteConfig.DELETE,
                  focusThreadId: RouteConfig.DELETE,
                },
              })}
            >
              Back to all activity
            </Button>
          </CardBody>
        </Card>
      )}
    </>
  );
};
